import { createAppointment } from "app/redux/appointmentsSlice";
import { selectBusinessServices } from "app/redux/businessServicesSlice";
import { searchClients, selectClients } from "app/redux/clientsSlice";
import { selectActiveEmployees } from "app/redux/employeesSlice";
import { selectShops } from "app/redux/shopsSlice";
import { formatAsUTC } from "app/utils/formatDate";
import { localDateToUtc, setActiveShopTimeZone, shopTimeZoneFromShop } from "app/utils/timezone";
import { getFormValues } from "app/utils/getFormValues";
import { useQuery } from "app/utils/useQuery";
import { adjustDateToUTC } from "app/utils/formatTime";
import {
  addMinutes,
  differenceInMinutes,
  format,
  isValid,
  parse,
  set,
  startOfDay,
} from "date-fns";
import { find } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export const useCreateAppointmentScreen = ({
  pendingAppointment,
  setPendingAppointment,
  toggleAppointmentDrawer,
  getSchedulesOfEmployees,
  getWeeklySchedulesOfEmployees,
  selectedEmployee,
  calendarView,
  onCloseBookingModal,
  updateCalendarData
}: {
  pendingAppointment: any;
  setPendingAppointment: (pendingAppointment: any) => void;
  toggleAppointmentDrawer: (open: boolean) => void;
  getSchedulesOfEmployees: ({ currentDate }: { currentDate?: Date }) => void;
  getWeeklySchedulesOfEmployees: any;
  selectedEmployee: any;
  calendarView: string;
  onCloseBookingModal?:any;
  updateCalendarData?: any;
}) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const navigate = useNavigate();
  const employees = useSelector(selectActiveEmployees);
  const { clients } = useSelector(selectClients);
  const employee = find(
    employees,
    (employee) => employee.id === pendingAppointment?.employee.id
  );

  const shops = useSelector(selectShops);
  const shop = find(shops, { id: Number(query.get("shopId")) });
  const shopTimeZone = shopTimeZoneFromShop(shop);

  useEffect(() => {
    if (shopTimeZone) setActiveShopTimeZone(shopTimeZone);
  }, [shopTimeZone]);

  const businessServices = useSelector(selectBusinessServices);
  const [selectedDate, setSelectedDate] = useState(pendingAppointment?.start);
  const [isDateInputFocused, setIsDateInputFocused] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [availableServices, setAvailableServices] = useState(businessServices);
  const [selectedServices, setSelectedServices] = useState<any>([]);
  const [serviceSelectValue, setServiceSelectValue] = useState(null);
  const [createAppointmentLoading, setCreateAppointmentLoading] = useState(false)
  const [addServiceModal, setAddServiceModal] = useState<boolean>(false)
  const [addProductModal, setAddProductModal] = useState<boolean>(false)
  const [editingEmployeeInServiceIndex, setEditingEmployeeInServiceIndex] =
    useState<number | null>(null);
  const employeeSelectRef = useRef<any>(null);
  const [notes, setNotes] = useState("");

  const onHandleClientChange = (selectedOption: any) => {
    setSelectedClient({
      ...selectedOption,
      id: selectedOption.value,
      name: selectedOption.label,
    });

    setPendingAppointment((prev:any)=>({...prev,client:{...selectedOption,

          id: selectedOption.value,
          name: selectedOption.label,
        }}))
  };

  const onHandleAddService = (selectedOption: any) => {
    const employeeService = find(employee!?.services, (service) => service.businessService.id === selectedOption.value
    );
    setSelectedServices([...selectedServices, employeeService]);
    setPendingAppointment((prev:any)=>({...prev,services:[...selectedServices, employeeService]}))

    const updatedAvailableServices = availableServices.map((category) => ({
      ...category,
      services: category.services.filter(
        (service: any) => service.id !== selectedOption.value
      ),
    }));

    setAvailableServices(updatedAvailableServices);
    setServiceSelectValue(null); // Clear the Select component
  };
  const onRemoveService = (index: number) => {
    const serviceToRemove = selectedServices[index];
    const remainingService=selectedServices.filter(((s:any)=>s.id!==serviceToRemove.id))
    setSelectedServices([...remainingService]);
    setPendingAppointment((prev: any) => ({
      ...prev,
      services: [...remainingService],
    }));

    const updatedAvailableServices = availableServices.map((category) => ({
      ...category,
      services: category.services.filter(
          (service: any) => service.id !== serviceToRemove.id
      ),
    }));
    setAvailableServices(updatedAvailableServices);
    setServiceSelectValue(null);

  };

  const handleEmployeeChange = (serviceIndex: number, selectedOption: any) => {
    const updatedServices = [...selectedServices];
    const newEmployee = employees.find(
        (emp) => emp.id === selectedOption.value
    );
    const newEmployeeService = newEmployee?.services.find(
        (service) =>
            service.businessService.id ===
            updatedServices[serviceIndex].businessService.id
    );
    updatedServices[serviceIndex] = {
      ...updatedServices[serviceIndex],
      employee: newEmployee,
      id: newEmployeeService.id,
      price: newEmployeeService.price,
      priceCents: newEmployeeService.priceCents,
      status: newEmployeeService.status,
      duration: newEmployeeService.duration,
    };
    setSelectedServices(updatedServices);
    setTimeout(() => {
      setEditingEmployeeInServiceIndex(null);
    }, 0);
  };

  const onSubmit = async (event: any) => {
    event.preventDefault();

    const { startTime } = getFormValues(event.target);

    if (!selectedClient) {
      toast.error("Please select a client");
      return;
    }

    if (selectedServices.length === 0) {
      toast.error("Please add at least one service");
      return;
    }

    setCreateAppointmentLoading(true);

    try {
      // 1️⃣ Parse date & time
      const formattedDate = format(selectedDate, "yyyy-MM-dd");
      const parsedDate = parse(formattedDate, "yyyy-MM-dd", new Date());
      const parsedTime = parse(startTime, "HH:mm", parsedDate);

      const startDateTime = set(parsedDate, {
        hours: parsedTime.getHours(),
        minutes: parsedTime.getMinutes(),
        seconds: 0,
        milliseconds: 0
      });

      // 2️⃣ Calculate total duration
      const totalDuration = selectedServices.reduce(
          (sum: number, service: any) => sum + service.duration,
          0
      );

      // 3️⃣ Calculate end time
      const endDateTime = addMinutes(startDateTime, totalDuration);

      // 4️⃣ Build payload
      const payload: any = {
        // startTime: format(startDateTime, "yyyy-MM-dd HH:mm"),
        startTime: localDateToUtc(startDateTime, shopTimeZone),
        // endTime: format(endDateTime, "yyyy-MM-dd HH:mm"),
        endTime: localDateToUtc(endDateTime, shopTimeZone),
        name: selectedClient.name,
        email: selectedClient.email,
        // Needed to create a new client who has no email
        phone: selectedClient.phone,
        shopId: Number(query.get("shopId")),
        employeeId: selectedServices[0].employee.id, // assuming same employee
        serviceIds: selectedServices.map((s: any) => s.id),
        clientId: selectedClient.id,
        notes: notes
      };

      // 5️⃣ Create appointment
      await dispatch(createAppointment(payload) as any).unwrap();

      // 6️⃣ Refresh schedules
      const scheduleDate = adjustDateToUTC(format(selectedDate, "yyyy-MM-dd"));
      if (updateCalendarData) {
        await updateCalendarData(scheduleDate);
      } else {
        if (getSchedulesOfEmployees) {
          getSchedulesOfEmployees({
            currentDate: scheduleDate
          });
        }
        if (getWeeklySchedulesOfEmployees && selectedEmployee?.id) {
          await getWeeklySchedulesOfEmployees({
            employeeId: selectedEmployee.id,
            date: scheduleDate
          });
        }
      }


      toast.success("Appointment created successfully");
      toggleAppointmentDrawer(false);
      onCloseBookingModal?.();
      setPendingAppointment(null);
    } catch (error: any) {
      console.error(error);
      // The server's reason, e.g. "Patty already has an appointment at this time..."
      toast.error(error?.error || "Failed to create appointment. Please try again.");
    } finally {
      setCreateAppointmentLoading(false);
    }
  };




  const handleClientSearch = async (inputValue: string, callback: any) => {
    if (inputValue.length > 2) {
      try {
        const response = await dispatch(
          searchClients(inputValue) as any
        ).unwrap();
        const results =
          response.clients?.map((c: any) => ({
            value: c.id,
            label: c.name,
            phone: c.phone,
            email: c.email,
            createdAt: c.createdAt,
            avatar: c.avatar,
          })) || [];
        callback(results);
      } catch (error) {
        console.error("Error searching clients:", error);
      }
    } else {
      return [];
    }
  };

  const onDateChange = useCallback(
    (date: Date) => {
      if (!isValid(date)) {
        return;
      }
      const formattedDate = formatAsUTC(date, "yyyy-MM-dd");
      const formattedStartTime = format(pendingAppointment?.start, "HH:mm");
      const formattedEndTime = format(
        addMinutes(
          pendingAppointment?.start,
          differenceInMinutes(
            pendingAppointment?.end,
            pendingAppointment?.start
          )
        ),
        "HH:mm"
      ); // Add 30 minutes to the start time
      const formattedDateStartTime = `${formattedDate}T${formattedStartTime}`; // Combine both
      const formattedDateEndTime = `${formattedDate}T${formattedEndTime}`; // Combine both
      const dateTimeStartObject = new Date(formattedDateStartTime);
      const dateTimeEndObject = new Date(formattedDateEndTime);

      setSelectedDate(formattedDate);
      setPendingAppointment({
        ...pendingAppointment,
        start: dateTimeStartObject,
        end: dateTimeEndObject,
      });
      getSchedulesOfEmployees({ currentDate: startOfDay(dateTimeStartObject) });
      query.delete("date");
      query.set("date", formattedDate);
      navigate(`?${query.toString()}`);
    },
    [pendingAppointment]
  );

  const onTimeChange = useCallback(
    (startTime: string) => {
      if (
        !isValid(pendingAppointment?.start) ||
        !isValid(pendingAppointment?.end)
      ) {
        return;
      }

      // Format the selected date
      const formattedDate = format(new Date(selectedDate), "yyyy-MM-dd");

      // Combine date with new start and end times
      const formattedStartDateTime = `${formattedDate}T${startTime}`;
      const formattedDateEndTime = `${formattedDate}T${format(
        addMinutes(
          formattedStartDateTime,
          differenceInMinutes(
            pendingAppointment?.end,
            pendingAppointment?.start
          )
        ),
        "HH:mm"
      )}`; // Combine both

      // Convert to Date objects
      const dateTimeStartObject = new Date(formattedStartDateTime);
      const dateTimeEndObject = new Date(formattedDateEndTime);

      // Update the pending appointment state
      setPendingAppointment({
        ...pendingAppointment,
        start: dateTimeStartObject,
        end: dateTimeEndObject,
      });
    },
    [pendingAppointment]
  );



  useEffect(() => {
    return () => {
      setPendingAppointment(null);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        employeeSelectRef.current &&
        !employeeSelectRef.current.contains(event.target as Node)
      ) {
        setEditingEmployeeInServiceIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [employeeSelectRef]);

  useEffect(() => {
    if (employee && businessServices.length > 0) {
      const updatedAvailableServices = businessServices.map((category) => ({
        ...category,
        services: category.services.map((service: any) => {
          const employeeService = employee.services.find(
              (empService) => empService.businessService.id === service.id
          );
          return {
            ...service,
            status: employeeService ? employeeService.status : "inactive",
          };
        }),
      }));
      setAvailableServices(updatedAvailableServices);
    }
  }, [employee, businessServices]);





  return {
    employee,
    employees,
    clients,
    isDateInputFocused,
    setIsDateInputFocused,
    onSubmit,
    onHandleClientChange,
    selectedClient,
    setSelectedClient,
    selectedServices,
    onHandleAddService,
    selectedDate,
    setSelectedDate,
    serviceSelectValue,
    availableServices,
    editingEmployeeInServiceIndex,
    setEditingEmployeeInServiceIndex,
    handleEmployeeChange,
    employeeSelectRef,
    onRemoveService,
    handleClientSearch,
    onDateChange,
    onTimeChange,
    createAppointmentLoading,
    addServiceModal,
    setAddServiceModal,
    addProductModal,
    setAddProductModal,
    notes,
    setNotes
  };
};
